import React from 'react'
import { Heading, Text, Button, LoadingSpinner } from '../../components/ui'
import {
  buildBrokerStrategicAnalysisPrompt,
  streamMcpChat,
  type McpChatToolEvent,
} from '../../services/mcpChat'
import {
  SparklesIcon,
  ArrowPathIcon,
  StopIcon,
} from '@heroicons/react/24/outline'

type Props = {
  brokerId: string
  idScgp: number | null
  brokerName: string
}

const CockpitAiAnalysisTab: React.FC<Props> = ({ brokerId, idScgp, brokerName }) => {
  const [running, setRunning] = React.useState(false)
  const [reply, setReply] = React.useState('')
  const [toolEvents, setToolEvents] = React.useState<McpChatToolEvent[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const abortRef = React.useRef<AbortController | null>(null)

  React.useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  React.useEffect(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setRunning(false)
    setReply('')
    setToolEvents([])
    setError(null)
  }, [brokerId])

  const stop = () => {
    abortRef.current?.abort()
    abortRef.current = null
    setRunning(false)
  }

  const generate = async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setRunning(true)
    setReply('')
    setToolEvents([])
    setError(null)

    const prompt = buildBrokerStrategicAnalysisPrompt({ brokerId, idScgp, brokerName })

    try {
      await streamMcpChat(
        [{ role: 'user', content: prompt }],
        {
          onTool: (event) => {
            setToolEvents((prev) => [...prev, event])
          },
          onToken: (content) => {
            setReply((prev) => prev + content)
          },
          onDone: (event) => {
            if (event.reply) setReply(event.reply)
          },
          onError: (message) => {
            setError(message)
          },
        },
        controller.signal,
      )
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        setError('Análise cancelada.')
      } else {
        const message = err instanceof Error ? err.message : 'Falha ao gerar análise.'
        setError(message)
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
      }
      setRunning(false)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading as="h3" size="xl" className="text-gray-900 dark:text-white flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Análise IA
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Gera uma análise estratégica e histórica do corretor via agente MCP (insumo das tools; sem repetir as outras abas).
          </Text>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <Button type="button" variant="outline" onClick={stop} className="flex items-center gap-1">
              <StopIcon className="h-4 w-4" />
              Parar
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void generate()}
              className="flex items-center gap-1"
            >
              {reply ? (
                <>
                  <ArrowPathIcon className="h-4 w-4" />
                  Regenerar
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Gerar análise
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {running && toolEvents.length > 0 && (
        <div className="rounded-md bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3">
          <Text size="xs" color="muted" className="uppercase tracking-wide mb-2 block">
            Consultas em andamento
          </Text>
          <ul className="space-y-1 max-h-32 overflow-y-auto">
            {toolEvents.map((ev, idx) => (
              <li key={`${ev.name}-${idx}`} className="text-sm text-gray-700 dark:text-gray-300">
                {ev.message || `Consultando ${ev.name}…`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {running && !reply && (
        <div className="flex items-center gap-2 py-6 justify-center">
          <LoadingSpinner />
          <Text size="sm" color="muted">
            Analisando dados do corretor…
          </Text>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{error}</Text>
        </div>
      )}

      {reply && (
        <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap text-gray-800 dark:text-gray-100 leading-relaxed">
          {reply}
        </div>
      )}

      {!running && !reply && !error && (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
          <Text color="muted">
            Clique em &quot;Gerar análise&quot; para solicitar o resumo padrão deste corretor ao agente.
          </Text>
        </div>
      )}
    </div>
  )
}

export default CockpitAiAnalysisTab
